
import { useNavigate } from "react-router-dom";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { useValidatedCoursePrice } from "./useValidatedCoursePrice";
import { useState } from "react";
import { enrollUserInCourse } from "@/lib/user-courses";
import MontonioPaymentInterface from "./MontonioPaymentInterface";

interface MontonioPaymentSectionProps {
  courseId: string | null;
  user: any;
  coursePrice: number;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  showTestModeAlert?: boolean;
}

// Define an interface for the payment payload
interface MontonioPaymentPayload {
  courseId: string;
  userId: string;
  amount: number;
  currency: string;
  paymentMethod?: string;
  selectedBank?: string | null;
}

const MontonioPaymentSection = ({
  courseId,
  user,
  coursePrice,
  loading,
  setLoading,
  showTestModeAlert = false
}: MontonioPaymentSectionProps) => {
  const navigate = useNavigate();
  const [redirecting, setRedirecting] = useState(false);
  const { validatedPrice, isPriceValid, formattedPrice } = useValidatedCoursePrice(courseId, coursePrice);

  // Handle Montonio payment
  const handleMontonioPayment = async (paymentInfo?: {
    paymentMethod: string | null;
    selectedBank: string | null;
  }) => {
    // -- Debug: show start of call
    console.log("[Montonio] handleMontonioPayment clicked", {
      user,
      courseId,
      coursePrice,
      validatedPrice,
      paymentInfo
    });

    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please log in to complete the purchase",
        variant: "destructive",
      });
      return;
    }

    if (!courseId) {
      toast({
        title: "Course Error",
        description: "No course selected",
        variant: "destructive",
      });
      return;
    }

    const price = validatedPrice || parseFloat(String(coursePrice));
    if (isNaN(price) || price <= 0) {
      toast({
        title: "Price Error",
        description: "Invalid course price. Please contact support.",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      setRedirecting(false); // Reset redirecting state

      // Convert price to cents for Montonio
      const amountInCents = Math.round(price * 100);
      
      // Prepare the payload with payment method data
      const payload: MontonioPaymentPayload = {
        courseId,
        userId: user.id,
        amount: amountInCents,
        currency: "EUR",
      };
      
      // Add payment method info if available
      if (paymentInfo) {
        if (paymentInfo.paymentMethod) {
          payload.paymentMethod = paymentInfo.paymentMethod;
        }
        if (paymentInfo.selectedBank) {
          payload.selectedBank = paymentInfo.selectedBank;
        }
      }
      
      // -- Debug: show payload for function
      console.log("[Montonio] Calling supabase.functions.invoke('montonio-payment') with:", payload);

      const { data, error } = await supabase.functions.invoke("montonio-payment", {
        body: payload,
      });

      // -- Debug: output complete response
      console.log("[Montonio] Complete response from montonio-payment:", data);

      if (error) {
        console.error("[Montonio] Payment function returned error:", error);
        throw new Error(error.message || "Failed to initialize payment");
      }

      if (!data) {
        throw new Error("No response data received from payment service");
      }

      // Handle test mode auto-enrollment
      if (data.test_mode && data.purchase_id && courseId) {
        try {
          console.log("[Montonio] Auto-enrolling user in course due to test mode payment");
          const enrollment = await enrollUserInCourse(user.id, courseId);
          console.log("[Montonio] Test mode enrollment result:", enrollment);

          if (!enrollment) {
            console.warn("[Montonio] Failed to auto-enroll user in test mode, but continuing with redirect");
          }
        } catch (enrollError) {
          console.error("[Montonio] Error auto-enrolling user in test mode:", enrollError);
        }
      }

      // FIXED: Use the correct property name (camelCase paymentUrl instead of snake_case payment_url)
      if (data?.paymentUrl) {
        setRedirecting(true);
        
        toast({
          title: "Redirecting to payment",
          description: "You will be redirected to Montonio to complete your payment",
        });

        console.log("[Montonio] Redirecting to payment URL:", data.paymentUrl);
        
        // Use a single, reliable redirection method
        window.location.assign(data.paymentUrl);
      } else {
        console.error("[Montonio] Missing payment URL in response:", data);
        throw new Error("No payment URL received");
      }
    } catch (error) {
      let errorMessage = "Failed to initialize payment";
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      console.error("[Montonio] Payment initialization error (frontend):", error);
      toast({
        title: "Payment Error",
        description: errorMessage,
        variant: "destructive",
      });
      setLoading(false);
      setRedirecting(false);
    }
  };

  return (
    <div className="mb-8">
      <h3 className="text-lg font-medium mb-2">Pay with Montonio</h3>
      <p className="text-sm text-gray-700 mb-4">
        Secure payment processing through Montonio.
      </p>
      <MontonioPaymentInterface 
        onSubmit={handleMontonioPayment}
        amount={formattedPrice}
        isTestMode={showTestModeAlert}
      />
    </div>
  );
};

export default MontonioPaymentSection;
